import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: transform `rexel_vendmast` raw rows into
 * `ap_suppliers` + `co_companies` domain rows.
 *
 * Stage 2 of the VENDMAST ingest pipeline. Runs after `master_data_bulk_insert`
 * has populated `rexel_vendmast`. For each row in the latest batch:
 *   - Creates a `co_companies` record for the vendor
 *   - Creates an `ap_suppliers` record linked to that company
 *   - Rows without a full name are skipped and counted
 *
 * Scoping: `updatedAfter` (typically wired from upstream `startedAt`) limits
 * the rexel_vendmast rows considered to those touched by the current run.
 */
export const transformVendmastSuppliersStep = defineStepConfig({
  type: "transform_vendmast_suppliers",
  name: "Transform VENDMAST → Suppliers",
  description:
    "Reads rexel_vendmast rows updated by the current run, creates ap_suppliers + co_companies for each vendor code.",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "ISO timestamp — only process rows updated after this time. " +
          "Typically wired from the upstream master_data_bulk_insert's startedAt output.",
      ),
  }),
  outputSchema: z.object({
    totalRows: z
      .number()
      .describe("Total number of rexel_vendmast rows processed"),
    created: z
      .number()
      .describe("Number of ap_suppliers + co_companies pairs created"),
    skippedNoFullName: z
      .number()
      .describe(
        "Number of rows skipped because the vendor record had no full name",
      ),
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "Pass-through of the input timestamp so downstream steps can scope to the current run.",
      ),
  }),
  configSchema: z.object({
    batchSize: z
      .number()
      .min(1)
      .max(5000)
      .prefault(500)
      .describe("Number of rows to process per batch"),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
